import { useState } from "react";
import Logo from '../components/Logo';
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../context/CampaignContext";

const OPTIONS = [
  { id:"search",  icon:"🔍", title:"Search Ads",        desc:"Show your business when people are actively searching on Google", benefits:["High intent traffic","Best for leads and sales","Fast results"], note:"Recommended for beginners and small budgets", noteColor:"#059669", noteBg:"#dcfce7" },
  { id:"youtube", icon:"▶️", title:"YouTube Ads",       desc:"Promote your business using video on YouTube", benefits:["Great for brand awareness","Visual storytelling","Scales well over time"], note:"Better if you already have a video ready", noteColor:"#d97706", noteBg:"#fef3c7" },
];

const COMPARISON = [
  { label:"Best for",      search:"Leads & Sales",      youtube:"Awareness & Views" },
  { label:"Budget needed", search:"Low ($5–$20/day)",   youtube:"Medium ($10–$30/day)" },
  { label:"Needs video?",  search:"No",                 youtube:"Yes" },
  { label:"Results speed", search:"Fast",               youtube:"Gradual" },
  { label:"Beginner-safe?",search:"✅ Yes",              youtube:"⚠️ If you have video" },
];

const navItems = [
  { id:"home",      icon:"🏠", label:"Home",          path:"/dashboard" },
  { id:"build",     icon:"⚙️", label:"Build Campaign", path:"/setup" },
  { id:"guide",     icon:"📋", label:"Launch Guide",   path:"/launch-guide" },
  { id:"templates", icon:"📁", label:"Templates",      path:"/dashboard" },
  { id:"profile",   icon:"👤", label:"Profile",        path:"/dashboard" },
];

export default function Screen4() {
  const navigate = useNavigate();
  const { updateSelections } = useCampaign();
  const [selected, setSelected]       = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [activeNav, setActiveNav]     = useState("build");

  const handleSelect = (id) => {
    setSelected(id);
    setShowWarning(id==="youtube");
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button style={s.backBtn} onClick={()=>navigate(-1)}>← Back</button>
          <Logo size={32} showText={true} textSize={16} />
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={s.stepPill}>Step 3 of 7</span>
          <div style={s.avatar}>AN</div>
        </div>
      </header>

      <div style={s.progressStrip}>
        <div style={s.barBg}><div style={{...s.barFill,width:"43%"}} /></div>
      </div>

      <div style={s.body}>
        <div style={s.twoCol}>
          {/* Left */}
          <div style={s.colLeft}>
            <div style={s.stepTag}>Step 3 of 7</div>
            <h1 style={s.heading}>Choose the Right Campaign for Your Goal</h1>
            <p style={s.sub}>We'll guide you to the best option based on your business, budget, and experience level.</p>

            <div style={s.recCard}>
              <div style={s.recBadge}>⭐ Recommended for You</div>
              <div style={s.recTitle}>Search Ads</div>
              <div style={s.recBody}>
                {["You want leads — Search targets people ready to act","Your budget is limited — Search works with smaller spend","You're a beginner — Search is simpler to manage"].map((r,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:8}}>
                    <span style={{width:6,height:6,borderRadius:"50%",background:"#6366f1",flexShrink:0,marginTop:6,display:"block"}} />
                    <span style={{fontSize:13,color:"#334155",fontFamily:"sans-serif",lineHeight:1.5}} dangerouslySetInnerHTML={{__html:r}} />
                  </div>
                ))}
              </div>
              <div style={{fontSize:12,color:"#475569",fontFamily:"sans-serif",lineHeight:1.5,borderTop:"1px solid #c7d2fe",paddingTop:12,fontStyle:"italic"}}>
                This gives you the best chance of getting results quickly without wasting money.
              </div>
            </div>

            <button style={s.compareToggle} onClick={()=>setShowCompare(v=>!v)}>
              {showCompare?"▲ Hide":"▼ Show"} Search vs YouTube comparison
            </button>
            {showCompare && (
              <div style={s.compareBox}>
                <div style={{display:"flex",background:"#f8faff",borderBottom:"1px solid #e2e8f0"}}>
                  <span style={{flex:1,padding:"10px 12px",fontSize:12,fontWeight:800,fontFamily:"sans-serif"}} />
                  <span style={{flex:1,padding:"10px 12px",fontSize:12,fontWeight:800,color:"#6366f1",fontFamily:"sans-serif",textAlign:"center"}}>🔍 Search</span>
                  <span style={{flex:1,padding:"10px 12px",fontSize:12,fontWeight:800,color:"#dc2626",fontFamily:"sans-serif",textAlign:"center"}}>▶️ YouTube</span>
                </div>
                {COMPARISON.map((row,i)=>(
                  <div key={i} style={{display:"flex",borderBottom:"1px solid #f1f5f9",background: i%2===0?"#f8faff":"#fff"}}>
                    <span style={{flex:1,padding:"10px 12px",fontSize:12,color:"#64748b",fontFamily:"sans-serif",fontWeight:600}}>{row.label}</span>
                    <span style={{flex:1,padding:"10px 12px",fontSize:12,color:"#1e293b",fontFamily:"sans-serif",textAlign:"center"}}>{row.search}</span>
                    <span style={{flex:1,padding:"10px 12px",fontSize:12,color:"#1e293b",fontFamily:"sans-serif",textAlign:"center"}}>{row.youtube}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right */}
          <div style={s.colRight}>
            {OPTIONS.map(opt=>{
              const isSel = selected===opt.id;
              return (
                <button key={opt.id} style={{...s.optCard,borderColor: isSel?"#6366f1":"#e2e8f0",background: isSel?"linear-gradient(135deg,#eef2ff 0%,#f0f9ff 100%)":"#fff",boxShadow: isSel?"0 6px 28px rgba(99,102,241,0.18)":"0 2px 10px rgba(0,0,0,0.05)"}} onClick={()=>handleSelect(opt.id)}>
                  {opt.id==="search" && <div style={{position:"absolute",top:14,right:-28,background:"#6366f1",color:"#fff",fontSize:10,fontWeight:800,padding:"4px 36px",fontFamily:"sans-serif",transform:"rotate(35deg)",letterSpacing:"0.5px"}}>⭐ Recommended</div>}
                  <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:16}}>
                    <div style={{width:48,height:48,borderRadius:12,background:"linear-gradient(135deg,#eef2ff,#e0f2fe)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:24}}>{opt.icon}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:18,fontWeight:800,fontFamily:"sans-serif",color: isSel?"#4f46e5":"#0f172a",marginBottom:4}}>{opt.title}</div>
                      <div style={{fontSize:13,color:"#64748b",fontFamily:"sans-serif",lineHeight:1.5}}>{opt.desc}</div>
                    </div>
                    <div style={{width:24,height:24,borderRadius:"50%",border:`2px solid ${isSel?"#6366f1":"#cbd5e1"}`,background: isSel?"#6366f1":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {isSel && <span style={{color:"#fff",fontSize:12,fontWeight:800}}>✓</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                    {opt.benefits.map((b,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background: isSel?"#6366f1":"#94a3b8",flexShrink:0,display:"block"}} />
                        <span style={{fontSize:13,color:"#334155",fontFamily:"sans-serif"}}>{b}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:12,fontWeight:600,padding:"8px 14px",borderRadius:8,color:opt.noteColor,background:opt.noteBg}}>
                    {opt.noteColor==="#059669"?"✅":"💡"} {opt.note}
                  </div>
                </button>
              );
            })}

            {showWarning && (
              <div style={{background:"#fff7ed",border:"1.5px solid #fbbf24",borderRadius:14,padding:"18px 20px"}}>
                <div style={{fontSize:14,fontWeight:800,color:"#b45309",fontFamily:"sans-serif",marginBottom:8}}>⚠️ Are you sure?</div>
                <div style={{fontSize:13,color:"#78350f",fontFamily:"sans-serif",lineHeight:1.55,marginBottom:14}}>YouTube ads may not be the best option for <strong>lead generation</strong> with a <strong>small budget</strong>.</div>
                <button style={{width:"100%",padding:"11px",borderRadius:10,background:"#6366f1",color:"#fff",border:"none",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",marginBottom:8}} onClick={()=>{setSelected("search");setShowWarning(false);}}>Switch to Search Ads (Recommended)</button>
                <button style={{width:"100%",padding:"10px",borderRadius:10,background:"none",border:"1.5px solid #fbbf24",color:"#b45309",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"sans-serif"}} onClick={()=>setShowWarning(false)}>Keep YouTube Ads anyway</button>
              </div>
            )}
          </div>
        </div>

        <div style={{height:120}} />
      </div>

      {/* Fixed CTA */}
      <div style={s.fixedFooter}>
        <button style={{...s.fixedCta,opacity: selected?1:0.38,cursor: selected?"pointer":"not-allowed"}} onClick={()=>{ if(selected){ updateSelections({ campaignType: selected }); navigate("/campaign-builder"); } }}>
          Continue →
        </button>
        {!selected && <p style={{textAlign:"center",fontSize:12,color:"#94a3b8",fontFamily:"sans-serif",margin:"8px 0 0"}}>Select a campaign type above to continue.</p>}
      </div>

      <nav style={s.bottomNav}>
        {navItems.map(n=>{
          const on = activeNav===n.id;
          return <button key={n.id} style={{...s.navItem,color: on?"#6366f1":"#94a3b8"}} onClick={()=>{setActiveNav(n.id);navigate(n.path);}}>
            <span style={{fontSize:20}}>{n.icon}</span>
            <span style={{fontSize:10,fontFamily:"sans-serif",fontWeight: on?700:400,color: on?"#6366f1":"#94a3b8"}}>{n.label}</span>
          </button>;
        })}
      </nav>
    </div>
  );
}

const s = {
  page:         {minHeight:"100vh",background:"#f8faff",fontFamily:"'Georgia',serif",display:"flex",flexDirection:"column"},
  header:       {display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 28px",background:"#fff",borderBottom:"1px solid #e2e8f0",boxShadow:"0 1px 8px rgba(0,0,0,0.05)",position:"sticky",top:0,zIndex:300},
  logo:         {display:"flex",alignItems:"center",gap:10},
  mark:         {width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",fontWeight:800,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  logoTxt:      {fontSize:16,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif"},
  stepPill:     {fontSize:12,fontWeight:700,color:"#6366f1",background:"#eef2ff",padding:"4px 12px",borderRadius:20,fontFamily:"sans-serif"},
  avatar:       {width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#f59e0b,#fbbf24)",color:"#fff",fontWeight:700,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  backBtn:      {background:"none",border:"none",fontSize:13,fontWeight:600,color:"#6366f1",cursor:"pointer",fontFamily:"sans-serif",padding:"6px 8px"},
  progressStrip:{background:"#fff",borderBottom:"1px solid #f1f5f9",padding:"10px 28px"},
  barBg:        {height:4,borderRadius:99,background:"#e2e8f0",overflow:"hidden"},
  barFill:      {height:"100%",borderRadius:99,background:"linear-gradient(90deg,#6366f1,#818cf8)"},
  body:         {flex:1,padding:"36px 28px 0",maxWidth:1000,margin:"0 auto",width:"100%",boxSizing:"border-box"},
  twoCol:       {display:"flex",gap:48,alignItems:"flex-start",flexWrap:"wrap"},
  colLeft:      {flex:"0 0 300px",minWidth:240},
  colRight:     {flex:1,minWidth:280,display:"flex",flexDirection:"column",gap:18},
  stepTag:      {fontSize:11,fontWeight:800,color:"#6366f1",background:"#eef2ff",display:"inline-block",padding:"4px 12px",borderRadius:20,fontFamily:"sans-serif",marginBottom:14,letterSpacing:"0.5px"},
  heading:      {fontSize:26,fontWeight:700,color:"#0f172a",margin:"0 0 12px",lineHeight:1.2},
  sub:          {fontSize:14,color:"#64748b",lineHeight:1.65,margin:"0 0 24px",fontFamily:"sans-serif"},
  recCard:      {background:"linear-gradient(135deg,#eef2ff,#f0fdf4)",border:"1.5px solid #c7d2fe",borderRadius:16,padding:"20px 20px",marginBottom:16},
  recBadge:     {fontSize:10,fontWeight:800,color:"#6366f1",fontFamily:"sans-serif",letterSpacing:"0.5px",marginBottom:8},
  recTitle:     {fontSize:20,fontWeight:800,color:"#1e293b",marginBottom:12},
  recBody:      {marginBottom:14},
  compareToggle:{background:"none",border:"none",fontSize:12,fontWeight:700,color:"#6366f1",fontFamily:"sans-serif",cursor:"pointer",padding:"6px 0",marginBottom:8},
  compareBox:   {background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden",marginBottom:8},
  optCard:      {width:"100%",padding:"22px 22px",borderRadius:18,border:"2px solid",cursor:"pointer",textAlign:"left",transition:"all 0.25s ease",position:"relative",overflow:"hidden",boxSizing:"border-box"},
  fixedFooter:  {position:"fixed",bottom:60,left:0,right:0,background:"rgba(248,250,255,0.97)",backdropFilter:"blur(8px)",borderTop:"1px solid #e2e8f0",padding:"14px 28px",zIndex:200},
  fixedCta:     {width:"100%",maxWidth:940,display:"block",margin:"0 auto",padding:"15px",borderRadius:12,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",border:"none",fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",boxShadow:"0 4px 20px rgba(99,102,241,0.35)"},
  bottomNav:    {position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #e2e8f0",display:"flex",justifyContent:"space-around",padding:"6px 0 10px",boxShadow:"0 -2px 16px rgba(0,0,0,0.06)",zIndex:300},
  navItem:      {display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"4px 10px"},
};